import { type ApiError as ApiErrorBody, type AuthResponseDto, type ErrorCode } from '@spin/shared';

export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
    readonly status = 0,
    readonly retryAfterMs?: number,
    readonly details?: { field: string; message: string }[],
  ) {
    super(message);
  }

  static from(body: Partial<ApiErrorBody> & { details?: ApiError['details'] }, status: number): ApiError {
    return new ApiError(
      (body.code ?? 'INTERNAL_ERROR') as ErrorCode,
      body.message ?? 'Request failed',
      status,
      body.retryAfterMs,
      body.details,
    );
  }
}

type Reauth = () => Promise<string | null>;

/** Small fetch client: bearer token, JSON, stable error codes, one transparent re-login when the token expires. */
class Http {
  private token: string | null = null;
  private reauth: Reauth | null = null;

  setToken(token: string | null): void {
    this.token = token;
  }

  getToken(): string | null {
    return this.token;
  }

  onReauth(fn: Reauth): void {
    this.reauth = fn;
  }

  async request<T>(method: string, path: string, body?: unknown, retry = true): Promise<T> {
    let res: Response;
    // A Blob (a photo) goes as it is, with its own type; anything else as JSON.
    const raw = body instanceof Blob;
    try {
      res = await fetch(`/api${path}`, {
        method,
        headers: {
          ...(body !== undefined
            ? { 'content-type': raw ? body.type || 'application/octet-stream' : 'application/json' }
            : {}),
          ...(this.token ? { authorization: `Bearer ${this.token}` } : {}),
        },
        body: body === undefined ? undefined : raw ? body : JSON.stringify(body),
      });
    } catch {
      throw new ApiError('SERVICE_UNAVAILABLE', 'Network error');
    }
    if (res.status === 401 && retry && this.reauth) {
      const fresh = await this.reauth();
      if (fresh) {
        this.token = fresh;
        return this.request<T>(method, path, body, false);
      }
    }
    const text = await res.text();
    const json = text ? (JSON.parse(text) as unknown) : undefined;
    if (!res.ok) throw ApiError.from((json ?? {}) as ApiErrorBody, res.status);
    return json as T;
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>('GET', path);
  }
  post<T>(path: string, body: unknown = {}): Promise<T> {
    return this.request<T>('POST', path, body);
  }
  patch<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>('PATCH', path, body);
  }
  put<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>('PUT', path, body);
  }

  /** A file the API serves behind the token (e.g. a payment receipt for admins). */
  async blob(path: string): Promise<Blob> {
    const res = await fetch(`/api${path}`, {
      headers: this.token ? { authorization: `Bearer ${this.token}` } : {},
    });
    if (!res.ok) {
      const text = await res.text();
      throw ApiError.from((text ? JSON.parse(text) : {}) as ApiErrorBody, res.status);
    }
    return res.blob();
  }
  delete<T>(path: string): Promise<T> {
    return this.request<T>('DELETE', path);
  }
}

export const http = new Http();

export async function login(initData: string): Promise<AuthResponseDto> {
  return http.request<AuthResponseDto>('POST', '/auth/telegram', { initData }, false);
}

/** Random id for idempotent actions (gifts, chat, payments). */
export function clientId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}
